<template>
  <div class="layer-rundown">
    <draggable
      v-model="posts"
      ref="tgbDropArea"
      class="layer-draggable draggable tgbPostDrop tgbTagboardDrop tgbDropArea scroll-x"
      role="listbox"
      tabindex="0"
      revert-on-spill="true"
      :move="checkMove"
      @start="onStart"
      @unchoose="onUnchoose"
      v-bind="{
        group: { name: 'socialGraphic', put: allowedSourceGroups },
        animation: 200,
        touchStartThreshold: 10,
      }"
      data-test="producer-social-graphic-dropzone"
    >
      <tagboard
        v-if="containsTagboard"
        class="tagboard"
        :tagboard-id="tagboardId"
        @remove-tagboard="$emit('delete-tagboard')"
      />

      <template v-else>
        <post
          v-for="post in posts"
          :key="`${post.network}:${post.post_id}`"
          :root-item="rootItem"
          :item="post"
          :queued-post="queuedPost"
          :layout="layout"
          @select="$emit('post-clicked', post.post_id)"
          @delete="$emit('delete-post', post.post_id)"
          @play-video="playVideo"
          @toggle-video="togglePlaying"
          @play-preview-video="playVideo(true)"
          @toggle-preview-video="togglePlaying(true)"
        />
      </template>

      <div v-if="!posts.length" class="tgbDropAreaHelp">
        <div
          v-if="!posts.length || isDragOver"
          class="tgbPostDropHelp tgbDropAreaHelp"
        >
          <p v-if="isMultiPostDisplay">
            Drag and drop a full Board here
          </p>
          <p v-else>
            Drag and drop a Board or posts here
          </p>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { debounce } from 'lodash-es';

import PostUtils from '@tagboard/helpers/post-utils';
import MediaProxy from '@tagboard/helpers/media-proxy';
import ConduitEventsService from '../../../services/conduitEventsService';
import { normalizePostForEvents } from '../../../utils/helpers';

import Tagboard from './TimelineSocialTagboard.vue';
import Post from '../post/TimelinePost.vue';

export default {
  name: 'SocialPostDropzone',

  components: {
    Post,
    Tagboard,
  },

  props: {
    // List of social posts currently attached to graphic (Vue 3)
    modelValue: {
      type: Array,
      default: () => [],
    },

    // Currently selected post ID. Will be shown when graphic is live.
    queuedPost: {
      type: String,
      default: '',
    },

    // Parent timeline item
    layout: {
      type: Object,
      required: true,
    },

    // Root timeline item (for graphic groups)
    rootItem: {
      type: Object,
      default: () => null,
    },

    // Whether this is the currently live layout
    isCurrentLayout: {
      type: Boolean,
      default: false,
    },

    // The `eventSource` helps distinguish source of the event for conduit.
    // 'click' → user explicitly clicked a post
    // 'element' → layout was taken live with a post already queued
    // 'auto-advance' → queued post changed via auto-advance
    eventSource: {
      type: String,
      default: null,
    },

    // Whether this is a multi-post display (grid, waterfall, carousel, mural)
    // Multi-post displays require a full tagboard and don't accept individual posts
    isMultiPostDisplay: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDragOver: false,
    };
  },

  computed: {
    allowedSourceGroups() {
      // Return a function that checks if the source group is allowed
      return (to, from) => {
        // Multi-post displays only accept tagboards, not individual posts
        const allowed = this.isMultiPostDisplay
          ? ['tagboard', 'socialGraphic']
          : ['tagboard', 'posts', 'socialGraphic'];

        return allowed.includes(from.options.group.name);
      };
    },

    posts: {
      get() {
        return this.modelValue ?? [];
      },

      set(v) {
        this.$emit('input', v);
        this.$emit('update:modelValue', v);
      },
    },

    tagboardId() {
      if (!this.containsTagboard) {
        return null;
      }

      const [post] = this.posts;
      return post._id || post.id;
    },

    containsTagboard() {
      return this.posts?.length && this.posts[0].type === 'tagboard';
    },

    postLookupTable() {
      return this.$store.state.posts.lookupTable;
    },

    production() {
      return this.$store.state.production.production;
    },

    productionId() {
      return this.production._id;
    },

    owner() {
      return this.production.owner;
    },

    isPlaying() {
      return this.$store.state.production.production.playing;
    },

  },

  created() {
  // Debounce the queuedPostHandler to mitigate rapid toggling issues (only on click).
  // When a post is clicked, the postId in the store toggles back and forth quickly(if the post is already live and isPlaying is true.)
  // This rapid change can trigger the watcher multiple times in quick succession, leading to unintended behavior.
  // The unintended change in the queuedPost state can retrigger the watcher with
  // the incorrect eventSource becuase it triggers twice after the click.
    this.debouncedQueuedPostHandler = debounce(this.queuedPostHandler, 300, {
      leading: true,
      trailing: false,
    });
  },

  beforeUnmount() {
    if (this.debouncedQueuedPostHandler && this.debouncedQueuedPostHandler.cancel) {
      this.debouncedQueuedPostHandler.cancel();
    }
  },

  /**
  * When post updates (either to a new post or cleared), we trigger a tracking event for Conduit.
  * - If a new post is queued: event is 'on'
  * - If the post is unqueued: event is 'off'
  * After tracking, we emit a clear signal to reset the source so it doesn't carry over to future updates.
  */
  watch: {
    queuedPost(newVal, oldVal) {
      this.debouncedQueuedPostHandler(newVal, oldVal);
    },
  },

  methods: {
    ...mapActions([
      'addToPostsLookupTable',
      'updateTimeline',
    ]),

    queuedPostHandler(newVal, oldVal) {
      if (!this.isCurrentLayout || newVal === oldVal) {
        this.$emit('clear-event-source');
        return;
      }

      const value = newVal ? 'on' : 'off';
      const postId = newVal || oldVal;
      const source = this.eventSource || 'auto-advance';

      const isClickedWhileAlreadyPlaying =
        this.isPlaying && this.isCurrentLayout && source === 'click';

      // only track when the post actually gets sent to live output.
      if (!isClickedWhileAlreadyPlaying) {
        this.trackLivePost(postId, value, source);
      }

      this.$emit('clear-event-source');
    },

    playVideo(preview) {
      if (preview) {
        this.$store.dispatch('playPreviewVideo');
      } else {
        this.$store.dispatch('playPostVideo');
      }
    },

    togglePlaying(preview) {
      if (preview) {
        this.$store.dispatch('togglePreviewVideo');
      } else {
        this.$store.dispatch('toggleLiveVideo');
      }
    },

    onStart() {
      const dropzones = document.querySelectorAll('.tgbTagboardDrop, .tgbPostDrop');
      for (let i = 0; i < dropzones.length; i += 1) {
        dropzones[i].classList.add('tgbDropArea--active');
      }
    },

    checkMove(e) {
      if (this.$refs.tgbDropArea.$el === e.to.closest('.tgbDropArea')) {
        // skip the other checks if we are reordering
        return true;
      }

      // In vue-draggable-next, the element might be directly on draggedContext
      const draggedElement = e.draggedContext?.element || e.draggedContext;
      if (!draggedElement) {
        return true; // Allow if we can't determine the type
      }

      const { type, post_id: postId } = draggedElement;
      const hasContent = Boolean(e.relatedContext?.list?.length);
      const isInList = e.relatedContext?.list?.some((i) => i?.post_id === postId);
      const hasTagboard = e.relatedContext?.list?.some((i) => i.type === 'tagboard');
      const tagboardInvolved = (hasContent && type === 'tagboard') || hasTagboard;
      const isIndividualPost = type !== 'tagboard';
      const notAllowed = tagboardInvolved
        || isInList
        || (this.isMultiPostDisplay && isIndividualPost);

      const errorMessages = {
        tagboard: 'A full board cannot be presented with other content.',
        duplicate: 'Duplicate posts not allowed.',
        multiPostOnly: 'Multi-post Social Displays require a full board.',
      };

      if (notAllowed) {
        e.to.classList.add('tgbDropArea--no');
        e.dragged.style.cursor = 'not-allowed';

        if (this.isMultiPostDisplay && isIndividualPost) {
          this.error = errorMessages.multiPostOnly;
        } else {
          this.error = errorMessages[tagboardInvolved ? 'tagboard' : 'duplicate'];
        }

        return false;
      }

      this.error = false;
      return true;
    },

    onUnchoose(e) {
      e.item.style.cursor = 'pointer';

      if (this.error) {
        this.errorToast(this.error);
      }

      document.querySelectorAll('.tgbDropArea--no').forEach((el) => {
        el.classList.remove('tgbDropArea--no');
        this.error = false;
      });

      document.querySelectorAll('.tgbDropArea--active').forEach((el) => {
        el.classList.remove('tgbDropArea--active');
      });
    },

    postText(postId) {
      const post = this.postLookupTable[postId];
      return post?.text || 'no text';
    },

    isVideo(postId) {
      const post = this.postLookupTable[postId];
      return post?.post_type === 'video' && (post.videos && post.videos.length);
    },

    photoUrl(postId) {
      const post = this.postLookupTable[postId];

      if (!post) {
        return '';
      }

      if (post.post_type === 'photo' && post.photos?.length) {
        let url = post.photos[0].m;

        if (post.network === 'facebook') {
          url = post.photos[0].l || post.photos[0].m;
        } else if (post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(post, 'm');
        }

        return MediaProxy.genUrl(url) || url;
      }

      if (post.videos?.length) {
        let url = post.videos[0].p;

        if (post.network === 'youtube') {
          url = post.videos[0].t;
        } else if (post.network === 'instagram') {
          url = PostUtils.instagramMediaUrl(post, 'm');
        }

        return MediaProxy.genUrl(url) || url;
      }

      return '';
    },

    networkIconClass(postId) {
      const post = this.postLookupTable[postId];
      if (!post.network) {
        return '';
      }

      if (post.network === 'instagram-stories') {
        return 'fab fa-instagram';
      }

      if (post.network === 'twitter') {
        return 'fab fa-x-twitter';
      }

      const networkIcons = {
        facebook: 'fab fa-facebook-f',
        instagram: 'fab fa-instagram',
        tiktok: 'fab fa-tiktok',
        twitch: 'fab fa-twitch',
        youtube: 'fab fa-youtube',
        threads: 'fab fa-threads',
        vimeo: 'fab fa-vimeo',
        x: 'fab fa-x-twitter',
      };

      return networkIcons[post.network] || '';
    },

    trackLivePost(queuedPost, value, eventSource) {
      if (!queuedPost) return;

      const post = this.posts.find(p => p.timeline_id === queuedPost);
      if (!post) return;

      const postId = post.post_id;
      const postPermalink = this.postLookupTable?.[postId]?.permalink;

      ConduitEventsService.track(
        'producer.toggled.live-post',
        {
          value,
          eventSource,
          production_id: this.production.id,
          post_id: postId,
          network: post.network,
          permalink: postPermalink,
          post: normalizePostForEvents(post),
          connectedScreens: this.$store.state.screens.connectedScreens,
          connectedScreensCount: this.$store.getters.connectedScreensCount,
          hasConnectedScreens: this.$store.getters.hasConnectedScreens,
        },
        {
          ownerId: this.owner,
          userId: this.$store.state.user.id,
          isStaff: this.$store.getters.isStaff,
        },
      );
    },
  },
};
</script>
